/* ==========================================================================
   DTF / UV DTF order screen

   Drives the format cards, size pills, the quantity stepper and volume tiers,
   the artwork service and turnaround choices, the live summary, the info tabs
   and the gang sheet dialog. Configuration comes from
   snippets/dtf-order-screen.liquid as window.DTFOrderConfig.

   Pricing: roll tier prices arrive already resolved to cents per metre
   (cfg.tiers, keyed by roll variant id). They are what the Shopify automatic
   discounts charge, so the page never computes a discount of its own. An empty
   list means volume pricing is off and the flat price is quoted.

   Add to cart is three requests, not one form post: the print itself, then the
   artwork service and the rush charge as their own lines when chosen. They
   share an underscore property so the cart can be read as one job, and the
   underscore keeps it off the customer's invoice.
   ========================================================================== */

(function () {
  'use strict';

  var cfg = window.DTFOrderConfig;
  var form = document.getElementById('DTFOrderForm');
  if (!cfg || !form) return;

  var $ = function (id) { return document.getElementById(id); };
  var all = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  /* ── data ──────────────────────────────────────────────────────────── */

  var byId = {};
  cfg.variants.forEach(function (v) { byId[String(v.id)] = v; });

  function sheetName(v) { return v.title.split(' ')[0]; }
  function sheetDims(v) { return (v.title.split('(')[1] || '').replace(')', '').replace('mm', ' mm'); }
  function rollWidth(v) { return (v.title.split('×')[1] || '').replace('mm)', '').trim(); }

  /* "A4:300:0.21" = an A4 sheet uses 0.21 m of the 300 mm roll. Used only to
     spot when the same artwork would be cheaper on a roll. */
  var sheetRolls = {};
  String(cfg.sheetRolls || '').split(',').forEach(function (part) {
    var bits = part.split(':');
    if (bits.length === 3) sheetRolls[bits[0].trim()] = { width: bits[1].trim(), len: parseFloat(bits[2]) };
  });

  function rollByWidth(width) {
    for (var id in cfg.tiers) {
      if (byId[id] && rollWidth(byId[id]) === width) return id;
    }
    return null;
  }

  /* Price table for a roll, quantity-from ascending, first row the base price. */
  function ladder(rollId) {
    var base = byId[rollId].price;
    var rows = [{ min: 1, price: base }];
    (cfg.tiers[rollId] || []).forEach(function (t) { rows.push({ min: t.qty, price: t.price }); });
    rows.forEach(function (r, i) { r.max = i + 1 < rows.length ? rows[i + 1].min - 1 : Infinity; });
    return rows;
  }

  function tierIndex(rows, qty) {
    for (var i = rows.length - 1; i >= 0; i--) if (qty >= rows[i].min) return i;
    return 0;
  }

  /* ── formatting ────────────────────────────────────────────────────── */

  /* Same shape as the store's money format ("R 1,234.00"), so prices written
     by Liquid and prices written here read the same side by side. */
  function money(cents) {
    var parts = (cents / 100).toFixed(2).split('.');
    return 'R ' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }
  function moneyShort(cents) {
    return cents % 100 === 0 ? money(cents).slice(0, -3) : money(cents);
  }

  function addWorkdays(n) {
    var d = new Date();
    while (n > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) n--;
    }
    return d;
  }
  function day(d) {
    return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  /* ── state ─────────────────────────────────────────────────────────── */

  var state = {
    group: cfg.startGroup,
    sheetId: cfg.firstSheet ? String(cfg.firstSheet) : null,
    qty: 1,
    art: null,
    artQty: 1,
    rush: null,
    nudge: null
  };

  function isRoll() { return state.group !== 'sheet'; }
  function currentVariant() { return byId[isRoll() ? state.group : state.sheetId]; }

  function unitPrice() {
    var v = currentVariant();
    if (!isRoll()) return v.price;
    var rows = ladder(state.group);
    return rows[tierIndex(rows, state.qty)].price;
  }

  function formatLabel() {
    var v = currentVariant();
    return isRoll() ? rollWidth(v) + ' mm roll' : sheetName(v) + ' sheet (' + sheetDims(v) + ')';
  }

  function unitWord(qty) {
    if (isRoll()) return qty === 1 ? 'metre' : 'metres';
    return qty === 1 ? 'sheet' : 'sheets';
  }

  /* ── render ────────────────────────────────────────────────────────── */

  var tiersEl = $('DTFOTiers');
  var sizesEl = $('DTFOSizes');

  function render() {
    var v = currentVariant();
    if (!v) return;
    var roll = isRoll();
    var unit = unitPrice();
    var printTotal = unit * state.qty;
    var saving = roll ? (v.price - unit) * state.qty : 0;
    var artTotal = state.art ? state.art.price * state.artQty : 0;
    var rushTotal = state.rush ? state.rush.price : 0;
    var total = printTotal + artTotal + rushTotal;

    /* hidden fields the main cart line is built from */
    $('DTFVariantId').value = v.id;
    $('DTFQtyHidden').value = state.qty;
    $('DTFOTurnaround').value = state.rush ? 'Rush' : 'Standard';
    $('DTFOArtProp').value = state.art ? state.art.name + ' × ' + state.artQty : 'Free check';

    /* step 1 */
    all('[data-dtfo-group]').forEach(function (b) {
      var on = b.getAttribute('data-dtfo-group') === state.group;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.classList.toggle('is-selected', on);
    });

    /* step 2 */
    $('DTFOStep2').textContent = roll ? 'How many metres?' : (sizesEl ? 'Which size, and how many?' : 'How many sheets?');
    if (sizesEl) {
      sizesEl.hidden = roll;
      all('[data-dtfo-sheet]', sizesEl).forEach(function (b) {
        var on = b.getAttribute('data-dtfo-sheet') === state.sheetId;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.classList.toggle('is-selected', on);
      });
    }
    renderTiers(roll);

    var qtyInput = $('DTFQty');
    if (String(state.qty) !== qtyInput.value) qtyInput.value = state.qty;
    $('DTFQtyDown').disabled = state.qty <= 1;
    $('DTFQtyUnit').textContent = unitWord(state.qty);
    $('DTFOUnitPrice').textContent = money(unit);
    $('DTFOUnitWord').textContent = roll ? 'per metre' : 'per ' + sheetName(v) + ' sheet';
    renderNudge(roll, v, printTotal);

    /* step 3 */
    all('[data-dtfo-art]').forEach(function (b) {
      var on = state.art ? b.getAttribute('data-dtfo-art') === state.art.key : b.getAttribute('data-dtfo-art') === 'ready';
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.classList.toggle('is-selected', on);
    });
    $('DTFOArtQty').hidden = !state.art;
    if (state.art) {
      $('DTFOArtQtyLabel').textContent = state.art.key === 'fix' ? 'How many files need fixing?' : 'How many designs need redrawing?';
      $('DTFOArtCount').textContent = state.artQty;
      $('DTFOArtTotal').textContent = money(artTotal);
      $('DTFOArtDown').disabled = state.artQty <= 1;
    }

    /* step 4 */
    all('[data-dtfo-speed]').forEach(function (b) {
      var on = (b.getAttribute('data-dtfo-speed') === 'rush') === !!state.rush;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.classList.toggle('is-selected', on);
    });

    /* summary */
    var rows = [
      ['Format', formatLabel()],
      ['Quantity', state.qty + ' ' + unitWord(state.qty) + ' × ' + money(unit)]
    ];
    if (saving > 0) rows.push(['Volume saving', '− ' + money(saving), 'is-saving']);
    rows.push(['Artwork', state.art ? state.art.name + ' × ' + state.artQty + ' · ' + money(artTotal) : 'Free check']);
    rows.push(['Turnaround', state.rush ? 'Rush · ' + money(rushTotal) : 'Standard']);
    var fileName = fileLabel();
    rows.push(['File', fileName || 'Not added yet', fileName ? '' : 'is-missing']);

    var sum = $('DTFOSumRows');
    sum.textContent = '';
    rows.forEach(function (r) {
      var row = document.createElement('div');
      row.className = 'dtfo-sum__row' + (r[2] ? ' ' + r[2] : '');
      var dt = document.createElement('dt');
      dt.textContent = r[0];
      var dd = document.createElement('dd');
      dd.textContent = r[1];
      row.appendChild(dt);
      row.appendChild(dd);
      sum.appendChild(row);
    });

    all('[data-dtfo-total]').forEach(function (el) { el.textContent = money(total); });
    all('[data-dtfo-add]').forEach(function (b) {
      if (!b.classList.contains('is-busy')) b.textContent = 'Add to cart · ' + money(total);
      b.disabled = !v.available;
    });
    $('DTFOBarMeta').textContent = state.qty + ' ' + unitWord(state.qty) + ' · ' + formatLabel();

    var canvas = $('DTFOGangCanvas');
    if (canvas) {
      if (roll) {
        var w = rollWidth(v);
        canvas.textContent = w + ' mm wide × 1000 mm long for each metre (' + Math.round(w * 300 / 25.4) + ' × 11811 px at 300 DPI)';
      } else {
        var mm = sheetDims(v).replace(' mm', '').split('×');
        canvas.textContent = sheetDims(v) + ' (' + Math.round(mm[0] * 300 / 25.4) + ' × ' + Math.round(mm[1] * 300 / 25.4) + ' px at 300 DPI)';
      }
    }
  }

  function renderTiers(roll) {
    if (!tiersEl) return;
    var rows = roll ? ladder(state.group) : [];
    if (rows.length < 2) { tiersEl.hidden = true; return; }

    var active = tierIndex(rows, state.qty);
    var base = rows[0].price;
    tiersEl.textContent = '';
    rows.forEach(function (r, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dtfo-pill' + (i === active ? ' is-selected' : '');
      b.setAttribute('aria-pressed', i === active ? 'true' : 'false');
      var pct = Math.round((1 - r.price / base) * 100);
      [
        ['dtfo-pill__small', r.max === Infinity ? r.min + '+ m' : r.min + '–' + r.max + ' m'],
        ['dtfo-pill__big', moneyShort(r.price)],
        ['dtfo-pill__small', pct ? 'Save ' + pct + '%' : 'Standard']
      ].forEach(function (p) {
        var s = document.createElement('span');
        s.className = p[0];
        s.textContent = p[1];
        b.appendChild(s);
      });
      b.addEventListener('click', function () { state.qty = r.min; render(); });
      tiersEl.appendChild(b);
    });
    tiersEl.hidden = false;
  }

  function renderNudge(roll, v, printTotal) {
    var box = $('DTFONudge');
    state.nudge = null;
    var text = '';
    var btn = '';

    if (roll) {
      var rows = ladder(state.group);
      var i = tierIndex(rows, state.qty);
      if (rows.length > 1 && i < rows.length - 1) {
        var next = rows[i + 1];
        var more = next.min - state.qty;
        text = 'Add ' + more + ' more ' + (more === 1 ? 'metre' : 'metres') + ' and every metre drops to ' + moneyShort(next.price) + '.';
        btn = 'Make it ' + next.min + ' m';
        state.nudge = { group: state.group, qty: next.min };
      } else if (rows.length > 1) {
        text = 'You are on our best price per metre.';
      }
    } else if (state.qty >= 2) {
      var map = sheetRolls[sheetName(v)];
      var rollId = map ? rollByWidth(map.width) : null;
      if (rollId && byId[rollId].available) {
        var metres = Math.max(1, Math.ceil(state.qty * map.len - 1e-9));
        var lr = ladder(rollId);
        var rollCost = lr[tierIndex(lr, metres)].price * metres;
        if (rollCost < printTotal) {
          text = state.qty + ' ' + sheetName(v) + ' sheets cost ' + money(printTotal) + '. The same artwork fits on ' +
            metres + ' m of the ' + rollWidth(byId[rollId]) + ' mm roll for ' + money(rollCost) +
            ', saving you ' + money(printTotal - rollCost) + '.';
          btn = 'Switch to ' + metres + ' m roll';
          state.nudge = { group: rollId, qty: metres };
        }
      }
    }

    if (!text) { box.hidden = true; return; }
    $('DTFONudgeText').textContent = text;
    var b = $('DTFONudgeBtn');
    b.hidden = !state.nudge;
    b.textContent = btn;
    box.hidden = false;
  }

  /* Static parts that only need the tier data once. */
  function renderOnce() {
    var best = null;
    for (var id in cfg.tiers) {
      var rows = ladder(id);
      var last = rows[rows.length - 1].price;
      if (rows.length > 1) {
        var from = document.querySelector('[data-dtfo-roll-from="' + id + '"]');
        if (from) from.innerHTML = moneyShort(rows[0].price) + ' <small>per metre, down to ' + moneyShort(last) + '</small>';
        if (byId[id].available && (best === null || last < best)) best = last;

        var table = document.querySelector('[data-dtfo-tier-table="' + id + '"] tbody');
        if (table) {
          table.textContent = '';
          rows.forEach(function (r) {
            var tr = document.createElement('tr');
            var pct = Math.round((1 - r.price / rows[0].price) * 100);
            [
              ['th', r.max === Infinity ? r.min + ' m or more' : r.min + '–' + r.max + ' m'],
              ['td', pct ? pct + '% off' : 'Standard price'],
              ['td', money(r.price)]
            ].forEach(function (c, ci) {
              var cell = document.createElement(c[0]);
              if (c[0] === 'th') cell.scope = 'row';
              if (ci === 2) cell.className = 'num';
              cell.textContent = c[1];
              tr.appendChild(cell);
            });
            table.appendChild(tr);
          });
        }
      }
    }
    var bestEl = $('DTFOBestRoll');
    if (bestEl && best !== null) {
      bestEl.textContent = ', down to ' + moneyShort(best);
      bestEl.hidden = false;
    }

    var std = $('DTFOStdWhen');
    if (std) std.textContent = 'Approve your artwork today and it is ready between ' + day(addWorkdays(cfg.stdFrom || 2)) + ' and ' + day(addWorkdays(cfg.stdTo || 5)) + '.';
    var rush = $('DTFORushWhen');
    if (rush) rush.textContent = 'Approve your artwork today and it is ready ' + day(addWorkdays(1)) + '.';
  }

  /* ── order events ──────────────────────────────────────────────────── */

  all('[data-dtfo-group]').forEach(function (b) {
    b.addEventListener('click', function () {
      state.group = b.getAttribute('data-dtfo-group');
      state.qty = 1;
      render();
    });
  });

  all('[data-dtfo-sheet]').forEach(function (b) {
    b.addEventListener('click', function () {
      state.sheetId = b.getAttribute('data-dtfo-sheet');
      render();
    });
  });

  $('DTFQtyDown').addEventListener('click', function () { state.qty = Math.max(1, state.qty - 1); render(); });
  $('DTFQtyUp').addEventListener('click', function () { state.qty += 1; render(); });
  $('DTFQty').addEventListener('input', function () {
    var n = parseInt(this.value, 10);
    state.qty = isNaN(n) || n < 1 ? 1 : n;
    render();
  });
  $('DTFQty').addEventListener('blur', function () { this.value = state.qty; });

  $('DTFONudgeBtn').addEventListener('click', function () {
    if (!state.nudge) return;
    state.group = state.nudge.group;
    state.qty = state.nudge.qty;
    render();
  });

  all('[data-dtfo-art]').forEach(function (b) {
    b.addEventListener('click', function () {
      var key = b.getAttribute('data-dtfo-art');
      state.art = key === 'ready' ? null : {
        key: key,
        name: b.getAttribute('data-name'),
        variantId: b.getAttribute('data-variant-id'),
        price: parseInt(b.getAttribute('data-price'), 10)
      };
      state.artQty = 1;
      render();
    });
  });
  $('DTFOArtDown').addEventListener('click', function () { state.artQty = Math.max(1, state.artQty - 1); render(); });
  $('DTFOArtUp').addEventListener('click', function () { state.artQty += 1; render(); });

  all('[data-dtfo-speed]').forEach(function (b) {
    b.addEventListener('click', function () {
      state.rush = b.getAttribute('data-dtfo-speed') === 'rush'
        ? { variantId: b.getAttribute('data-variant-id'), price: parseInt(b.getAttribute('data-price'), 10) }
        : null;
      render();
    });
  });

  /* Radio groups made of buttons: arrow keys move the choice, as with native radios. */
  all('.dtfo-cards, .dtfo-options, #DTFOSizes').forEach(function (group) {
    group.addEventListener('keydown', function (e) {
      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].indexOf(e.key) === -1) return;
      var items = all('[role="radio"]:not([disabled])', group);
      var i = items.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      var step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
      var next = items[(i + step + items.length) % items.length];
      next.focus();
      next.click();
    });
  });

  all('[data-dtfo-thumb]').forEach(function (t) {
    t.addEventListener('click', function () {
      var main = $('DTFMainImage');
      if (main) {
        main.src = t.getAttribute('data-dtfo-thumb');
        main.removeAttribute('srcset');
      }
      all('[data-dtfo-thumb]').forEach(function (k) { k.classList.remove('is-active'); });
      t.classList.add('is-active');
    });
  });

  /* ── artwork upload ────────────────────────────────────────────────── */

  var fileInput = $('dtf-file-input');
  var dropzone = $('dtf-dropzone');
  var linkInput = $('dtf-artwork-link');
  var errorEl = $('dtf-artwork-error');
  var MAX_BYTES = 5 * 1024 * 1024;

  function fileLabel() {
    if (fileInput && fileInput.files.length) return fileInput.files[0].name;
    if (linkInput && linkInput.value.trim()) return 'Link added';
    return '';
  }

  function showFile(file) {
    if (file.size > MAX_BYTES) {
      fileInput.value = '';
      alert('That file is over 5 MB. Please paste a shareable Google Drive, Dropbox or WeTransfer link instead.');
      if (linkInput) linkInput.focus();
      return;
    }
    $('dtf-dropzone-body').hidden = true;
    $('dtf-file-name').textContent = file.name + ' (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)';
    $('dtf-success').hidden = false;
    errorEl.hidden = true;
    render();
  }

  dropzone.addEventListener('dragover', function (e) { e.preventDefault(); dropzone.classList.add('is-over'); });
  dropzone.addEventListener('dragleave', function () { dropzone.classList.remove('is-over'); });
  dropzone.addEventListener('drop', function (e) {
    e.preventDefault();
    dropzone.classList.remove('is-over');
    if (!e.dataTransfer.files.length) return;
    try {
      var dt = new DataTransfer();
      dt.items.add(e.dataTransfer.files[0]);
      fileInput.files = dt.files;
    } catch (err) {
      /* Older browsers cannot be assigned a FileList; clicking still works. */
    }
    showFile(e.dataTransfer.files[0]);
  });
  fileInput.addEventListener('change', function () { if (this.files.length) showFile(this.files[0]); });
  $('dtf-remove-btn').addEventListener('click', function () {
    fileInput.value = '';
    $('dtf-success').hidden = true;
    $('dtf-dropzone-body').hidden = false;
    render();
  });
  linkInput.addEventListener('input', function () { errorEl.hidden = true; render(); });

  /* ── add to cart ───────────────────────────────────────────────────── */

  function check(res) {
    if (res.ok) return res.json();
    return res.json().then(
      function (j) { throw new Error(j.description || j.message || 'Could not add that to the cart.'); },
      function () { throw new Error('Could not add that to the cart.'); }
    );
  }

  function addLine(variantId, qty, group) {
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', qty);
    body.append('properties[For]', cfg.productTitle + ' — ' + formatLabel());
    body.append('properties[_dtf_group]', group);
    return fetch('/cart/add.js', { method: 'POST', body: body }).then(check);
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!fileLabel()) {
      errorEl.hidden = false;
      dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var buttons = all('[data-dtfo-add]');
    var cartError = $('DTFOCartError');
    buttons.forEach(function (b) { b.disabled = true; b.classList.add('is-busy'); b.textContent = 'Adding…'; });
    cartError.hidden = true;

    var group = 'dtf-' + Date.now().toString(36);
    var main = new FormData(form);
    main.append('properties[_dtf_group]', group);

    fetch('/cart/add.js', { method: 'POST', body: main })
      .then(check)
      .then(function () { if (state.art) return addLine(state.art.variantId, state.artQty, group); })
      .then(function () { if (state.rush) return addLine(state.rush.variantId, 1, group); })
      .then(function () { window.location.href = '/cart'; })
      .catch(function (err) {
        buttons.forEach(function (b) { b.disabled = false; b.classList.remove('is-busy'); });
        cartError.textContent = err.message + ' Please check your cart before trying again.';
        cartError.hidden = false;
        render();
      });
  });

  /* ── info tabs ─────────────────────────────────────────────────────── */

  var tabs = all('.dtfo-tab');
  function selectTab(tab, focus) {
    tabs.forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      $(t.getAttribute('aria-controls')).hidden = !on;
    });
    if (focus) tab.focus();
  }
  tabs.forEach(function (t, i) {
    t.addEventListener('click', function () { selectTab(t); });
    t.addEventListener('keydown', function (e) {
      var step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      selectTab(tabs[(i + step + tabs.length) % tabs.length], true);
    });
  });
  all('[data-dtfo-tab-link]').forEach(function (a) {
    a.addEventListener('click', function () {
      var tab = $('DTFOTab-' + a.getAttribute('data-dtfo-tab-link'));
      if (tab) selectTab(tab);
    });
  });

  /* ── gang sheet dialog ─────────────────────────────────────────────── */

  var dialog = $('DTFOGang');
  if (dialog && dialog.showModal) {
    all('[data-dtfo-open-gang]').forEach(function (b) {
      b.addEventListener('click', function () { dialog.showModal(); });
    });
    all('[data-dtfo-close]', dialog).forEach(function (b) {
      b.addEventListener('click', function () { dialog.close(); });
    });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
  } else {
    all('[data-dtfo-open-gang]').forEach(function (b) { b.hidden = true; });
  }

  /* ── link help modal (shared snippet) ──────────────────────────────── */

  var helpBtn = $('dtf-link-help-btn');
  var helpModal = $('dtf-link-help-modal');
  if (helpBtn && helpModal) {
    var helpClose = $('dtf-help-close');
    var openHelp = function () { helpModal.hidden = false; document.body.style.overflow = 'hidden'; helpClose.focus(); };
    var closeHelp = function () { helpModal.hidden = true; document.body.style.overflow = ''; helpBtn.focus(); };
    helpBtn.addEventListener('click', openHelp);
    helpClose.addEventListener('click', closeHelp);
    $('dtf-help-overlay').addEventListener('click', closeHelp);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !helpModal.hidden) closeHelp();
    });
  }

  /* ── go ────────────────────────────────────────────────────────────── */

  if (!currentVariant()) state.group = cfg.firstSheet ? 'sheet' : state.group;
  renderOnce();
  render();
})();
